import rehypeParse from 'rehype-parse';
import rehypeRemark from 'rehype-remark';
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import remarkStringify from 'remark-stringify';

/**
 * Converts Markdown to HTML.
 */
export function markdownToHtml(markdownText: string): string {
  const file = remark().use(remarkHtml).processSync(markdownText);
  return String(file);
}

/**
 * Converts HTML to Markdown. Used to transform rich-text editor content
 * before submitting to the API.
 */
export function htmlToMarkdown(htmlText: string): string {
  const file = remark()
    .use(rehypeParse, { emitParseErrors: true, duplicateAttribute: false })
    .use(rehypeRemark)
    .use(remarkStringify)
    .processSync(htmlText);
  return String(file).trimEnd();
}

export type ModuleName = { url: string; text: string };
export type GameContent = Record<string, boolean>;

/**
 * Generates the initial rich-text content for SendMessage when pre-populating
 * links from a game/module context. Converts `gameContent` keys into anchor
 * tags using the provided `getMessageContent` resolver.
 */
const NOT_FOUND = -1;

export function generateModifiedContent(
  gameContent: GameContent,
  getMessageContent: (moduleSlug: string) => ModuleName,
): string {
  const moduleNames: ModuleName[] = [];

  Object.entries(gameContent).forEach(([key, value]) => {
    if (value) {
      let result = '';
      if (key.startsWith('Module')) {
        const subModuleName = key.replace('Module-', '');
        const colonIndex = subModuleName.indexOf(':');
        if (colonIndex !== NOT_FOUND) {
          result = subModuleName.substring(colonIndex + 1).trim();
        }
      } else {
        // Extract content between brackets (e.g. "[foo]" → "foo")
        const match = key.match(/\[([^\]]+)\]/);
        result = match ? match[1]! : key;
      }
      moduleNames.push(getMessageContent(result));
    }
  });

  return moduleNames
    .map(
      ({ url, text }) =>
        `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`,
    )
    .join('<br>');
}

/** Quill editor formats supported in the SendMessage rich-text editor. */
export const richTextFormats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
] as const;

/**
 * Filters an array by a string field, case-insensitively.
 */
export function filterByField<T extends Record<string, unknown>>(
  array: T[],
  search: string,
  field: keyof T,
): T[] {
  if (!search) return array;
  return array.filter((entry) => {
    const fieldValue = entry[field];
    return (
      typeof fieldValue === 'string' &&
      fieldValue.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    );
  });
}
